import getTargetCouponTrackingInfo from '@/components/common/ProductCard/helper/get-target-coupon-tracking-info';
import useGetTargetProductCouponInfo from '@/components/common/ProductCard/hook/useGetTargetProductCouponInfo';
import { ProductCardProps } from '@/components/common/ProductCard/types';
import { BusEventKeys } from '@/constants/track';
import useCart from '@/store/shopping/index';
import { BaseProductType } from '@/types/product';
import { InViewOptions } from '@/types/track';
import { getProductCardExtraAttrs } from '@/utils/productFormat';
import { wrapCurrency } from '@/utils/tool';
import { isSayWeeeApp } from '@/utils/weeeapp';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useInView } from 'react-intersection-observer';

/**
 * @param options 埋点参数
 * 这里组件展示埋点统一使用BusEventKeys.productCardImpression，具体的埋点事件使用trackType区分(具体逻辑函数写在useProductCardImpressionTrack文件中)
 * 整个过程: 发送BusEventKeys.productCardImpression埋点事件 -> 匹配事件函数(useTrack.ts) -> 根据trackType匹配真正的埋点处理函数, 埋点参数均写在
 * options中，在具体的处理函数中自行处理
 *
 * 注意！！！！！！！！！！！！！！！！！！！
 * 即时发送和延迟发送参数不通用，注意设置正确trackType的值
 */
function useProductCardInview(originProps: Partial<ProductCardProps>) {
  const { ref, inView, entry } = useInView({
    threshold: 0.5
  });
  const targetCouponInfo = useGetTargetProductCouponInfo(originProps);
  const trackingCouponInfo = getTargetCouponTrackingInfo(originProps, targetCouponInfo);
  const [isReported, setIsReported] = useState(false);
  const timer = useRef(null);
  //-------
  const [positionInfoT2, setPositionInfoT2] = useState<any>({});
  const [newProps, setNewProps] = useState(originProps);
  const [impressionOption, setImpressionOption] = React.useState<InViewOptions | (() => InViewOptions)>({});

  useEffect(() => {
    const option =
      typeof originProps.impressionOption === 'function'
        ? originProps.impressionOption()
        : originProps.impressionOption || {};

    if (!!originProps.positionInfoT2) {
      const _positionInfoT2 = {
        ...originProps.positionInfoT2,
        context: originProps.positionInfoT2?.context
      };
      setPositionInfoT2(_positionInfoT2);
    }

    setImpressionOption(option);
    setNewProps(originProps);
  }, [originProps.impressionOption, originProps.data, originProps.positionInfoT2, originProps]);

  const { id2qty } = useCart();

  const getAdsCoParams = useCallback((productData: Partial<BaseProductType>) => {
    if (productData.is_sponsored && productData.ads_creative) {
      const params = new URLSearchParams(productData.ads_creative?.trackRedirectUrl);
      const allValues = params.getAll('utm_campaign');

      return {
        url: productData.ads_creative?.trackRedirectUrl,
        utm_campaign: allValues[allValues.length - 1]
      };
    }
    return {};
  }, []);

  const impressionOptionNew = {
    ...impressionOption,
    impressionOptionT2: {
      ...(positionInfoT2?.modSecPos || {}),
      co: {
        ...getAdsCoParams(newProps?.data),
        ...({
          ...newProps.data,
          base_price: newProps?.data?.base_price
            ? wrapCurrency(newProps?.data?.base_price)
            : newProps?.data?.base_price,
          price: newProps?.data?.price ? wrapCurrency(newProps?.data?.price) : newProps?.data?.price
        } || {}),
        index: positionInfoT2?.prodPos,
        qty_in_cart: id2qty?.[newProps?.data?.id || newProps?.data?.product_id],
        showActivity: newProps?.showActivity,
        showWeekly: newProps?.showWeekly,
        showLabel: newProps?.showLabel ?? true,
        tag_key: newProps?.data?.entrance_tag?.tag_key,
        tag_name: newProps?.data?.entrance_tag?.tag_name,
        discount_percentage: newProps?.data?.discount_percentage,
        ...trackingCouponInfo
      },
      ctx: {
        ...(positionInfoT2?.context || {}),
        tag_key: undefined,
        tag_name: undefined
      }
    },
    id: newProps?.data?.id || newProps?.data?.product_id,
    ads_creative: newProps?.data?.ads_creative || undefined
  };

  useEffect(() => {
    if (inView) {
      if (isReported) return;
      timer.current = setTimeout(() => {
        setIsReported(true);
        if (window.bus) {
          window.bus.emit(BusEventKeys.productCardImpression, impressionOptionNew);
        }
        clearTimeout(timer.current);
      }, 1000);
    }
    return () => clearTimeout(timer.current);
  }, [inView, isReported]);

  const extraAttrs = useMemo(() => {
    if (newProps?.data?.extra_attrs) {
      try {
        return JSON.parse(newProps?.data?.extra_attrs || '{}');
      } catch (error) {
        console.error('Failed to parse extra_attrs:', error);
      }
    }
    return null;
  }, [newProps?.data?.extra_attrs]);

  useEffect(() => {
    const handleImpression = () => {
      setIsReported(false);
    };
    if (isSayWeeeApp()) {
      document.addEventListener('onPageResume', handleImpression);
    }
    return () => {
      if (isSayWeeeApp()) {
        document.removeEventListener('onPageResume', handleImpression);
      }
    };
  }, []);

  return {
    ref,
    inView,
    entry,
    impressionOption,
    clickOption: {
      ...(positionInfoT2?.modSecPos || {}),
      co: {
        ...getAdsCoParams(newProps?.data),
        target_pos: positionInfoT2?.prodPos,
        target_nm: newProps?.data?.id || newProps?.data?.product_id,
        is_mkpl: newProps?.data?.biz_type === 'seller' ? true : false,
        is_fbw: newProps?.biz_type === 'fbw' ? true : false,
        recommendation_trace_id: newProps?.data?.recommend_trace_id || newProps?.data?.recommendation_trace_id,
        extra_attrs: extraAttrs,
        ...newProps?.co
      },
      ctx: {
        volume_price_support: newProps?.data?.volume_price_support ? true : false,
        extra_attrs: getProductCardExtraAttrs(newProps?.data),
        ...positionInfoT2?.context,
        ...trackingCouponInfo
      }
    }
  };
}

export default useProductCardInview;
